import type { PlumaManagedModalProps } from '@customerio/pluma-components/react';
import {
  Button,
  Form,
  FormLayout,
  Modal,
  ModalBody,
  ModalFooter,
  Text,
  TextArea,
} from '@customerio/pluma-components/react';
import { authStore } from '@/services/auth/store';
import { useStore } from '@nanostores/react';
import { z } from '@customerio/hydra-validation';

import { useZodForm } from '@/components/form';

import { useSubmitFeedback } from '../hooks/use-submit-feedback';

const generalFeedbackSchema = z
  .object({
    criticism: z.string(),
    praise: z.string(),
  })
  .refine((data) => (data.criticism?.length ?? 0) > 0 || (data.praise?.length ?? 0) > 0, {
    message: 'Please provide either criticism or praise',
    path: ['criticism'],
  });

interface GeneralFeedbackModalProps {
  initialFeedback?: string;
  message?: string;
}

export function GeneralFeedbackModal({
  data,
  onConfirm,
  onClose,
  modalState,
}: PlumaManagedModalProps<GeneralFeedbackModalProps>) {
  const auth = useStore(authStore);
  const submitFeedback = useSubmitFeedback();

  const form = useZodForm({
    schema: generalFeedbackSchema,
    defaultValues: { criticism: data.initialFeedback ?? '', praise: '' },
    mode: 'onChange',
  });

  const onSubmit = form.handleSubmit(async (values) => {
    try {
      await submitFeedback({
        ...values,
        source: 'General Feedback',
        replyTo: auth.user?.email ?? '',
      });

      onConfirm(values);
    } catch {
      // noop
    }
  });

  const { isValid, isSubmitting } = form.formState;

  if (auth.account == null || auth.user == null) {
    return null;
  }

  return (
    <Modal state={modalState} onClose={onClose} title="Share your feedback" size="md" shouldCloseOnOverlayClick={false}>
      <ModalBody>
        <Form id="general-feedback-form" onSubmit={onSubmit}>
          {data.message != null ? (
            <Text mb="200" color="subtle">
              {data.message}
            </Text>
          ) : null}
          <FormLayout>
            <TextArea
              {...form.registerPluma('criticism')}
              label="What can we improve?"
              placeholder="Customer.io would be better if…"
              minRows={3}
              maxRows={8}
              id="general-feedback-criticism-textarea"
            />

            <TextArea
              {...form.registerPluma('praise')}
              label="What are we doing well?"
              placeholder="I like…"
              minRows={3}
              maxRows={8}
              id="general-feedback-praise-textarea"
            />
          </FormLayout>
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button variant="tertiary" onClick={onClose} isDisabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          variant="primary"
          form="general-feedback-form"
          type="submit"
          isDisabled={!isValid}
          isLoading={isSubmitting}
        >
          Send message
        </Button>
      </ModalFooter>
    </Modal>
  );
}
